import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Edit,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  SmartToy,
  TrendingUp,
  Block,
  CheckCircle,
  AdminPanelSettings,
  Security,
  History,
  Notifications,
  Payment,
  Settings,
  Storage,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import AdvancedDBEditor from '../../components/Common/AdvancedDBEditor';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isAdmin: boolean;
  createdAt: string;
  lastLoginAt?: string;
  phone?: string;
  country?: string;
  totalBots: number;
  activeBots: number;
  totalTrades: number;
  totalVolume: number;
  subscriptionTier: 'free' | 'basic' | 'premium' | 'enterprise';
  emailVerified: boolean;
  phoneVerified: boolean;
  address?: string;
  city?: string;
  zipCode?: string;
  twoFactorEnabled: boolean;
  lastPasswordChange?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [profileWarningDismissed, setProfileWarningDismissed] = useState(false);

  const [formData, setFormData] = useState<Partial<User>>({});

  // Fetch user data
  const { data: userDetailsData, isLoading, error } = useQuery<any, Error>({
    queryKey: ['user', userId],
    queryFn: () => api.get(`/admin/users/${userId}`),
    onSuccess: (data) => {
      setFormData(data.user);
    },
  });

  // Extract user from the response
  const user = userDetailsData?.user;
  const hasDynamoDBProfile = userDetailsData?.hasDynamoDBProfile;

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: (updates: Partial<User>) => api.put(`/admin/users/${userId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      queryClient.invalidateQueries(['users']);
      setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      setIsEditing(false);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update user', severity: 'error' });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: () => api.delete(`/admin/users/${userId}`),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      setTimeout(() => navigate('/users'), 1500);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    },
  });

  // Create DynamoDB profile mutation
  const createProfileMutation = useMutation({
    mutationFn: () => api.post(`/admin/users/${userId}/profile`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId]);
      setSnackbar({ open: true, message: 'DynamoDB profile created successfully', severity: 'success' });
      setProfileWarningDismissed(true);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create DynamoDB profile', severity: 'error' });
    },
  });

  const handleSave = () => {
    updateUserMutation.mutate(formData);
  };

  const handleDelete = () => {
    deleteUserMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleInputChange = (field: keyof User, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/users')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            User Details
          </Typography>
        </Box>
        <Alert severity="error">
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
            Failed to load user details
          </Typography>
          <Typography variant="body2">
            {error?.message || 'An error occurred while fetching user details. Please try again.'}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/users')}
            >
              Back to Users
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/users')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            User Details
          </Typography>
        </Box>
        <Alert severity="warning">
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
            User not found
          </Typography>
          <Typography variant="body2">
            The user you're looking for doesn't exist or has been deleted.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/users')}
            sx={{ mt: 2 }}
          >
            Back to Users
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* DynamoDB Profile Warning Banner */}
      {!hasDynamoDBProfile && !profileWarningDismissed && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Box display="flex" gap={1}>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => createProfileMutation.mutate()}
                disabled={createProfileMutation.isLoading}
              >
                {createProfileMutation.isLoading ? 'Creating...' : 'Create Profile'}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => setProfileWarningDismissed(true)}
              >
                Dismiss
              </Button>
            </Box>
          }
        >
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
            No DynamoDB Profile Found
          </Typography>
          <Typography variant="body2">
            This user exists in Cognito but doesn't have a DynamoDB profile.
            This may limit some functionality. Click "Create Profile" to create one automatically.
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/users')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
          <Chip
            label={user.isActive ? 'Active' : 'Inactive'}
            color={user.isActive ? 'success' : 'default'}
            size="small"
          />
          {user.isAdmin && (
            <Chip
              label="Admin"
              color="secondary"
              size="small"
              icon={<AdminPanelSettings />}
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(user);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={updateUserMutation.isLoading}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete User
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
              >
                Edit User
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" icon={<Person />} iconPosition="start" />
          <Tab label="Account Settings" icon={<Settings />} iconPosition="start" />
          <Tab label="Security" icon={<Security />} iconPosition="start" />
          <Tab label="Activity" icon={<History />} iconPosition="start" />
          <Tab label="Billing" icon={<Payment />} iconPosition="start" />
          <Tab label="Advanced DB" icon={<Storage />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={formData.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                      InputProps={{
                        endAdornment: user.emailVerified && (
                          <Chip label="Verified" size="small" color="success" />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                      InputProps={{
                        endAdornment: user.phoneVerified && (
                          <Chip label="Verified" size="small" color="success" />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Address Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.zipCode || ''}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country || ''}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      disabled={!isEditing}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Statistics */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Trading Activity
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <SmartToy sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {user.totalBots}
                      </Typography>
                      <Typography variant="body2">Total Bots</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'success.main',
                        color: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {user.activeBots}
                      </Typography>
                      <Typography variant="body2">Active Bots</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'info.main',
                        color: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <TrendingUp sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {user.totalTrades.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">Total Trades</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'warning.main',
                        color: 'white',
                        borderRadius: 2,
                      }}
                    >
                      <TrendingUp sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        ${user.totalVolume.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">Total Volume</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Account Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Account Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Member Since
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Login
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString()
                          : 'Never'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Subscription Tier
                      </Typography>
                      <Chip
                        label={user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        User ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {user.id}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Account Settings Tab */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Account Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive ?? false}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Account Active"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Inactive accounts cannot log in or use the platform
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isAdmin ?? false}
                      onChange={(e) => handleInputChange('isAdmin', e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Admin Privileges"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Admins have full access to the admin dashboard
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subscription Tier</InputLabel>
                  <Select
                    value={formData.subscriptionTier || 'free'}
                    label="Subscription Tier"
                    onChange={(e) => handleInputChange('subscriptionTier', e.target.value)}
                    disabled={!isEditing}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Security Settings
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary="Email Verification"
                  secondary={user.emailVerified ? 'Verified' : 'Not verified'}
                />
                <Chip
                  label={user.emailVerified ? 'Verified' : 'Unverified'}
                  color={user.emailVerified ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <Phone />
                </ListItemIcon>
                <ListItemText
                  primary="Phone Verification"
                  secondary={user.phoneVerified ? 'Verified' : 'Not verified'}
                />
                <Chip
                  label={user.phoneVerified ? 'Verified' : 'Unverified'}
                  color={user.phoneVerified ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary={user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                />
                <Chip
                  label={user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  color={user.twoFactorEnabled ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <CalendarToday />
                </ListItemIcon>
                <ListItemText
                  primary="Last Password Change"
                  secondary={
                    user.lastPasswordChange
                      ? new Date(user.lastPasswordChange).toLocaleDateString()
                      : 'Never changed'
                  }
                />
              </ListItem>
            </List>
            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" color="warning" fullWidth>
                Force Password Reset
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Activity Tab */}
      <TabPanel value={activeTab} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity logs and history will be displayed here.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Billing Tab */}
      <TabPanel value={activeTab} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Billing Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payment methods and billing history will be displayed here.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Advanced DB Tab */}
      <TabPanel value={activeTab} index={5}>
        <AdvancedDBEditor
          entityType="user"
          entityId={userId || ''}
          onSaveSuccess={() => {
            queryClient.invalidateQueries(['user', userId]);
            setSnackbar({ open: true, message: 'Database entry updated successfully', severity: 'success' });
          }}
        />
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{user.firstName} {user.lastName}</strong>?
            This will permanently remove all user data, bots, and trading history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteUserMutation.isLoading}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetails;

